import { API_BASE as API_BASE_CONFIG } from './config'
import { CUSTOMER_VIEW_HEADER } from './AuthProvider'
/**
 * Sempirical API Client
 * ===============
 * All backend communication goes through here.
 * No hardcoded data anywhere else.
 *
 * Auth: attaches the bearer token AuthProvider stores (shared-password or per-user
 * Supabase session -- the server decides which it accepts, see XLR8_AUTH_MODE).
 * Without one the backend answers 401; there is no dev bypass.
 */

const API_BASE = API_BASE_CONFIG

// ─── Helpers ────────────────────────────────────────────────

async function getAuthHeaders() {
  const token = localStorage.getItem('xlr8_token')
  if (token) return { Authorization: `Bearer ${token}` }
  return {}
}

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`
  const authHeaders = await getAuthHeaders()
  const config = {
    // `cache: 'no-store'`: every status-polling call in this app (drain, probe
    // onboarding) hits the IDENTICAL URL every 5s -- the exact shape a browser is
    // most likely to serve from its HTTP cache instead of the network, with no
    // explicit signal either way. Confirmed live 2026-09-23: a genuinely
    // in-progress run (done:false, actively progressing server-side) read as
    // "looks completed" in the UI -- the same class of staleness this session
    // already had to work around by hand once with an explicit no-store fetch,
    // just never applied here at the one shared layer every call goes through.
    cache: 'no-store',
    ...options,
    // after ...options, so a caller's extra headers add to the auth headers, never replace them
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
  }
  // Don't set Content-Type for FormData (browser sets multipart boundary)
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type']
  }
  const res = await fetch(url, config)
  if (res.status === 401) {
    // Session expired, revoked or deactivated (R-B: the server re-checks the users row on
    // every request). AuthProvider's fetch wrapper has already tried one refresh; clear
    // the stored session HERE too before redirecting, so a dead token is never re-sent.
    localStorage.removeItem('xlr8_token')
    localStorage.removeItem('xlr8_refresh_token')
    localStorage.removeItem('xlr8_token_expires_at')
    if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
      window.location.href = '/'
    }
    throw new Error('Session expired. Please sign in again.')
  }
  if (res.status === 403) {
    // Check if this is an MFA enforcement rejection
    const body = await res.text()
    if (body.includes('mfa_required')) {
      // A staff session that has not passed TOTP yet -- finish MFA first
      if (window.location.pathname !== '/security/mfa') {
        window.location.href = '/security/mfa'
      }
      throw new Error('MFA verification required.')
    }
    throw new Error(`API 403: ${body}`)
  }
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`API ${res.status}: ${body}`)
  }
  return res.json()
}

function get(path) {
  return request(path)
}

function post(path, body) {
  return request(path, {
    method: 'POST',
    body: body instanceof FormData ? body : JSON.stringify(body),
  })
}

function put(path, body) {
  return request(path, { method: 'PUT', body: JSON.stringify(body) })
}

function del(path) {
  return request(path, { method: 'DELETE' })
}

// ─── Health ─────────────────────────────────────────────────

export async function getHealth() {
  return get('/api/health')
}

// ─── Customer Context ───────────────────────────────────────

export async function getCustomers() {
  return get('/api/context')
}

// Contracts (queue item 181) — admin-only, read-only evaluation of a stored contract
export async function getAdminContracts(customerId) {
  return get(customerId ? `/api/admin/contracts?customer_id=${encodeURIComponent(customerId)}` : '/api/admin/contracts')
}

export async function evaluateAdminContract(contractId, frequency, period) {
  return post(`/api/admin/contracts/${contractId}/evaluate`, { frequency, period })
}

// Customer portal — per-customer tab settings (PUT is admin-only) and the customer's own
// contracts. The contract endpoints refuse a non-admin unless the Contract tab is enabled.
export async function getPortalSettings(customerId) {
  return get(`/api/portal/${encodeURIComponent(customerId)}/settings`)
}

export async function getPortalDataMap(customerId) {
  return get(`/api/portal/${encodeURIComponent(customerId)}/data-map`)
}

export async function setPortalTabs(customerId, tabs) {
  // The tab editor is the admin's, not part of the customer view it sits on: an empty
  // customer-view header keeps this one call the admin's while the portal is previewed.
  return request(`/api/portal/${encodeURIComponent(customerId)}/settings`, {
    method: 'PUT', body: JSON.stringify({ tabs }), headers: { [CUSTOMER_VIEW_HEADER]: '' },
  })
}

export async function getPortalContracts(customerId) {
  return get(`/api/portal/${encodeURIComponent(customerId)}/contracts`)
}

export async function evaluatePortalContract(customerId, contractId, frequency, period) {
  return post(`/api/portal/${encodeURIComponent(customerId)}/contracts/${contractId}/evaluate`, { frequency, period })
}

// Employee Lifeline (queue item 219): one person's record as a timeline. Needs the
// view_employee_detail permission. A person is named by an OPAQUE reference the server
// hands out (picker, drill rows, Register rows) -- never an employee number or a company
// code in a URL (Scott, 2026-10-07).
export async function getEmployeeLifeline(customerId, employeeRef) {
  return get(`/api/portal/${encodeURIComponent(customerId)}/employees/${encodeURIComponent(employeeRef)}/lifeline`)
}

// The lifeline picker: the customer's companies (each with a company reference), then the
// people in one company matching a name or number search. No pay fields.
export async function getLifelineCompanies(customerId) {
  return get(`/api/portal/${encodeURIComponent(customerId)}/employees/companies`)
}

export async function searchLifelineEmployees(customerId, companyRef, q = '', includeFormer = false) {
  const params = new URLSearchParams({ company: companyRef, q, include_former: includeFormer ? 'true' : 'false' })
  return get(`/api/portal/${encodeURIComponent(customerId)}/employees?${params}`)
}

export async function getCustomer(customerId) {
  return get(`/api/context/${customerId}`)
}

// Every change needs `reason` (why). Send `expected_updated_at` (the record's
// `updated_at` as read) so a save over someone else's newer change is a 409, not
// a silent overwrite. Who made the change is the signed-in user, set by the server.
export async function updateCustomer(customerId, data) {
  return put(`/api/context/${customerId}`, data)
}

// Edit history of the customer context (staff only): who, when, what, why.
export async function getContextHistory(customerId, limit = 200) {
  return get(`/api/context/${encodeURIComponent(customerId)}/history?limit=${limit}`)
}

// Suggested changes to a customer's declared context. A customer sees its own;
// consultants and admins see all and approve or reject.
export async function getContextSuggestions(customerId, status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return get(`/api/context/${encodeURIComponent(customerId)}/suggestions${q}`)
}

export async function submitContextSuggestion(customerId, fieldPath, proposed, note) {
  return post(`/api/context/${encodeURIComponent(customerId)}/suggestions`, { field_path: fieldPath, proposed, note })
}

export async function approveContextSuggestion(customerId, suggestionId, reviewNote) {
  return post(`/api/context/${encodeURIComponent(customerId)}/suggestions/${suggestionId}/approve`, { review_note: reviewNote || null })
}

export async function rejectContextSuggestion(customerId, suggestionId, reviewNote) {
  return post(`/api/context/${encodeURIComponent(customerId)}/suggestions/${suggestionId}/reject`, { review_note: reviewNote || null })
}

export async function createCustomer(customerId, data) {
  return post(`/api/context/${customerId}`, data)
}

// Item 20 (TENANT IDS ARE COMPANY NAMES), the other half: NewCustomer.jsx used
// to generate the new customer's id itself (a slugified company name), which
// would have recreated the exact defect the 7 existing customers were just
// renamed off of. This endpoint takes no id -- the backend derives the next
// sequential one (the next tNNN) from what already exists and returns it.
export async function createCustomerAutoId(data) {
  return post('/api/context', data)
}

// A customer whose context record exists without its customers row (a create that failed
// half way): syncs, uploads and metrics refuse it until its setup is finished. The GET
// says whether it is (tenant_kind null = not); the POST finishes it, keeping any stored kind.
export async function getCustomerSetup(customerId) {
  return get(`/api/context/${encodeURIComponent(customerId)}/tenant-kind`)
}

export async function finishCustomerSetup(customerId) {
  return post(`/api/context/${encodeURIComponent(customerId)}/tenant-kind`, {})
}

// FIXED 2026-09-15 (item 20 investigation): this used to call
// /api/admin/customer/{id}/data, routers/admin.py's clear_customer_data --
// the OLDER, broken purge path with a hand-typed 4-table Supabase list and
// every failure silently swallowed (except: pass), exactly the disease that
// caused the original 26-of-29-tables-left-behind ghost-tenant incident.
// Now calls the real tool (services/tenant_purge.py via DELETE
// /api/context/{id}): the customer-scoped table list is DERIVED live from
// PostgREST's own schema, and a partial delete raises loudly instead of
// reporting success. dry_run=true (the default) returns the manifest --
// Supabase row counts, warehouse table list -- without deleting anything.
export async function previewDeleteCustomer(customerId) {
  return del(`/api/context/${customerId}?dry_run=true`)
}

export async function executeDeleteCustomer(customerId) {
  return del(`/api/context/${customerId}?dry_run=false`)
}

export async function getTerminology(customerId) {
  return get(`/api/context/${customerId}/terminology`)
}

export async function setSpineLevels(customerId, spineLevels) {
  return post(`/api/context/${customerId}/spine-levels`, { spine_levels: spineLevels })
}

export async function addTerminology(customerId, standardTerm, customerTerm) {
  return post(`/api/context/${customerId}/terminology`, { standard_term: standardTerm, customer_term: customerTerm })
}

export async function getClarifications(customerId) {
  return get(`/api/chat/clarifications/${customerId}`)
}

// ─── Chat (Core Workspace Endpoint) ─────────────────────────

/**
 * Main chat endpoint. This is the primary way the workspace talks to Sempirical.
 *
 * Returns: {
 *   success, answer, needs_clarification, clarification,
 *   is_analysis, analysis_mode, analysis_result,
 *   data, columns, row_count, sql, citations,
 *   request_id, processing_time_ms, error,
 *   thread_id, thread_saved, follow_up
 * }
 */
export async function chat(question, customerId, options = {}) {
  return post('/api/chat', {
    question,
    customer_id: customerId,
    ...options,
  })
}

export async function chatFeedback(requestId, feedback) {
  return post('/api/chat/feedback', { request_id: requestId, ...feedback })
}

// ─── Analysis ───────────────────────────────────────────────

export async function getAnalysisModes() {
  return get('/api/analyze/modes')
}

export async function runAnalysis(params) {
  return post('/api/analyze', params)
}

export async function compareFiles(params) {
  return post('/api/analyze/compare-files', params)
}

// Multipart: profile two uploaded files and get suggested key + field mappings
// (Lane 1 — /api/analyze/compare-preview). `files` = { sourceFile, targetFile }.
export async function comparePreview({ sourceFile, targetFile, customerId, sourceSheet, targetSheet }) {
  const fd = new FormData()
  fd.append('source_file', sourceFile)
  fd.append('target_file', targetFile)
  fd.append('customer_id', customerId || 'default')
  if (sourceSheet) fd.append('source_sheet', sourceSheet)
  if (targetSheet) fd.append('target_sheet', targetSheet)
  return post('/api/analyze/compare-preview', fd)
}

// Multipart: run the actual file comparison with confirmed key + compare fields
// (Lane 2 consultative output — /api/analyze/compare-files).
export async function compareFilesUpload({ sourceFile, targetFile, keyField, compareFields, fieldMap, customerId, sourceSheet, targetSheet, useContext }) {
  const fd = new FormData()
  fd.append('source_file', sourceFile)
  fd.append('target_file', targetFile)
  fd.append('key_fields', keyField)
  if (compareFields && compareFields.length) fd.append('compare_fields', compareFields.join(','))
  if (fieldMap && Object.keys(fieldMap).length) fd.append('field_map', JSON.stringify(fieldMap))
  fd.append('customer_id', customerId || 'default')
  if (sourceSheet) fd.append('source_sheet', sourceSheet)
  if (targetSheet) fd.append('target_sheet', targetSheet)
  if (useContext) fd.append('use_context', 'true')
  return post('/api/analyze/compare-files', fd)
}

// Build a formatted .xlsx reconciliation workbook from a compare result.
export async function exportCompareWorkbook(result) {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/analyze/compare-export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(result),
  })
  if (!res.ok) throw new Error('Could not build the workbook.')
  return res.blob()
}

// Persist a confirmed FIELD (column-name) mapping so the next compare pre-fills.
export async function createFieldMapping({ customerId, name, sourceSystem, targetSystem, pairs }) {
  return post('/api/mappings', {
    customer_id: customerId,
    name,
    mapping_type: 'field',
    source_system: sourceSystem || null,
    target_system: targetSystem || null,
    entries: pairs.map(p => ({ source_value: p.source_field, target_value: p.target_field })),
  })
}

export async function compareTruths(params) {
  return post('/api/analyze/compare', params)
}

export async function explainVariance(params) {
  return post('/api/analyze/explain', params)
}

export async function getAnalysisHistory(customerId) {
  return get(`/api/analyze/history/${customerId}`)
}

// ─── Data / Uploads ─────────────────────────────────────────

export async function getUploads(customerId) {
  return get(`/api/uploads/${customerId}`)
}

export async function uploadFile(file, customerId, truthType = 'actual', vendorId, productId, isSdmExport) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('customer_id', customerId)
  formData.append('truth_type', truthType)
  if (vendorId) formData.append('vendor_id', vendorId)
  if (productId) formData.append('product_id', productId)
  if (isSdmExport) formData.append('is_sdm_export', 'true')
  return post('/api/upload', formData)
}

export async function deleteUpload(uploadId) {
  return del(`/api/upload/${uploadId}`)
}

export async function hydrate(customerId) {
  return post(`/api/hydrate/${customerId}`, {})
}

export async function getHydrationStatus(customerId) {
  return get(`/api/hydrate/status/${customerId}`)
}

export async function getSchemas(customerId) {
  return get(`/api/admin/schemas/${customerId}`)
}

export async function getTablePreview(customerId, tableName, limit = 50, offset = 0) {
  return get(`/api/admin/schemas/${customerId}/tables/${tableName}/preview?limit=${limit}&offset=${offset}`)
}

// A plain <a>/window.open to this URL 404s through Vercel's SPA catch-all and
// can't carry the auth header anyway -- fetch as a blob instead, same as
// exportCompareWorkbook above.
export async function exportProductTablesReport(customerId, productUid = 'ukg_pro_wfm') {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}/api/admin/reports/product-tables/${customerId}?product_uid=${encodeURIComponent(productUid)}`, {
    headers: authHeaders,
  })
  if (!res.ok) throw new Error(`Could not build the report (${res.status}).`)
  return res.blob()
}

export async function buildContextGraph(customerId, force = true) {
  return post(`/api/admin/context-graph/${customerId}/build?force=${force}`)
}

export async function getContextGraphStatus(customerId) {
  return get(`/api/admin/context-graph/${customerId}/status`)
}

export async function getContextGraphRelationships(customerId, table) {
  const qs = table ? `?table=${encodeURIComponent(table)}` : ''
  return get(`/api/admin/context-graph/${customerId}/relationships${qs}`)
}

export async function getContextGraphMap(customerId) {
  return get(`/api/admin/context-graph/${encodeURIComponent(customerId)}/map`)
}

export async function getContextGraphJoinPath(customerId, fromTable, toTable) {
  const qs = new URLSearchParams({ from_table: fromTable, to_table: toTable }).toString()
  return get(`/api/admin/context-graph/${customerId}/join-path?${qs}`)
}

export async function getPaycodeCrosswalk() {
  return get('/api/admin/paycode-crosswalk')
}

export async function getCustomerPaycodeMapping(customerId) {
  return get(`/api/admin/paycode-crosswalk/${customerId}`)
}

export async function saveCustomerPaycodeMapping(customerId, rows) {
  return post(`/api/admin/paycode-crosswalk/${customerId}`, { rows })
}

// ─── Connectors ─────────────────────────────────────────────

export async function getConnections(customerId) {
  return get(`/api/connector/connections/${customerId}`)
}

export async function getConnectorVendors() {
  return get('/api/connector/vendors')
}

// ─── Developer Hub capture (item 81) ───────────────────────────
// Lets a customer's UKG Developer Hub category tree be captured
// without a curl command -- calls the same debug-dev-hub-* routes an
// engineer would otherwise have to hit by hand.

export async function debugDevHubCapture(customerId, { targetCategories, docPath, batchSize, hostname } = {}) {
  const params = new URLSearchParams()
  params.set('enumerate_domains', 'true')
  if (targetCategories) params.set('target_categories', targetCategories)
  if (docPath) params.set('doc_path', docPath)
  if (batchSize) params.set('batch_size', String(batchSize))
  if (hostname) params.set('hostname', hostname)
  return post(`/api/connector/debug-dev-hub-login/${customerId}?${params.toString()}`)
}

// Credential-free path (item 82 follow-up, 2026-09-22): uses the WFM connection's own
// already-stored username/password -- the same one the daily sync already uses -- instead
// of a separate Developer Hub login nobody has set up. Every real run measured so far
// requires MFA, so this is always a two-step call: start (returns a flow_id once the code
// is emailed) then complete (submits the code, resumes into the same capture).
export async function startWfmUiLogin(customerId, { targetCategories, batchSize } = {}) {
  const params = new URLSearchParams()
  params.set('enumerate_domains', 'true')
  if (targetCategories) params.set('target_categories', targetCategories)
  if (batchSize) params.set('batch_size', String(batchSize))
  return post(`/api/connector/debug-wfm-ui-login-start/${customerId}?${params.toString()}`)
}

export async function completeWfmUiLogin(customerId, flowId, code) {
  const params = new URLSearchParams({ flow_id: flowId, code })
  return post(`/api/connector/debug-wfm-ui-login-complete/${customerId}?${params.toString()}`)
}

export async function debugDevHubDrain(enumFlowId, { batchSize, maxBatches, runInBackground } = {}) {
  const params = new URLSearchParams()
  if (batchSize) params.set('batch_size', String(batchSize))
  if (maxBatches) params.set('max_batches', String(maxBatches))
  if (runInBackground !== undefined) params.set('run_in_background', String(runInBackground))
  return post(`/api/connector/debug-dev-hub-drain/${enumFlowId}?${params.toString()}`)
}

export async function debugDevHubDrainStatus(enumFlowId) {
  return get(`/api/connector/debug-dev-hub-drain-status/${enumFlowId}`)
}

// Scott, 2026-09-27: "I have no idea what debug raw hit means or where that would
// happen" -- debug-raw-hit (routers/connector.py) has existed since §19.66 and was
// never wired to any UI at all; the only way to use it was a raw curl call with a
// bearer token pulled out of DevTools, exactly the "F12 crap" the Export-to-Excel
// buttons already exist to avoid. Deliberately narrow here (GET only, no body) --
// this wraps ONLY the read-safe half of the real endpoint, for the "is this even
// enabled for this tenant" question it's being reached for right now, not a general
// arbitrary-write console.
export async function debugRawHitGet(customerId, vendorId, productId, path, { noPaging, maxChars } = {}) {
  const params = new URLSearchParams({
    vendor_id: vendorId, product_id: productId, endpoint_name: '_raw_hit_ui',
    path_override: path,
  })
  if (noPaging) params.set('no_paging', 'true')
  if (maxChars) params.set('max_chars', String(maxChars))
  return post(`/api/connector/debug-raw-hit/${customerId}?${params.toString()}`)
}

export async function getVendors() {
  return get('/api/vendors')
}

export async function getProducts(vendorId) {
  const params = vendorId ? `?vendor_id=${vendorId}` : ''
  return get(`/api/products${params}`)
}

export async function getPlatformConfig() {
  return get('/api/admin/platform-config')
}

export async function testConnection(params, customerId) {
  const url = customerId
    ? `/api/connector/test-connection?customer_id=${encodeURIComponent(customerId)}`
    : '/api/connector/test-connection'
  return post(url, params)
}

export async function saveConnection(customerId, params) {
  return post(`/api/connector/save-connection/${customerId}`, params)
}

export async function deleteConnection(customerId, vendorId, productId) {
  const resp = await fetch(`${API_BASE}/api/connector/delete-connection/${customerId}?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  })
  if (!resp.ok) throw new Error(`Delete failed: ${resp.status}`)
  return resp.json()
}

export async function syncConnector(customerId, vendorId, productId, endpoints = [], endpointParams = {}, fullHistory = false) {
  // Send both endpoint list and per-endpoint parameters. fullHistory: reload incremental
  // payroll-history endpoints IN FULL (the Data tab's Re-pull of a history table).
  const body = { endpoints, endpoint_params: endpointParams }
  if (fullHistory) body.full_history = true
  return post(`/api/connector/sync/${customerId}?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`, body)
}

// Re-run the RaaS report a loaded table came from and reload it (one finalize).
export async function repullRaasTable(customerId, table) {
  return post(`/api/customers/${encodeURIComponent(customerId)}/raas/repull`, { table })
}

export async function getDataHealth(customerId) {
  return get(`/api/connector/data-health/${customerId}`)
}

export async function getActiveJob(customerId, productId) {
  return get(`/api/connector/active-job/${encodeURIComponent(customerId)}/${encodeURIComponent(productId)}`)
}

export async function getActiveJobForCustomer(customerId) {
  return get(`/api/connector/active-job/${encodeURIComponent(customerId)}`)
}

export async function getSyncStatus(jobId) {
  return get(`/api/connector/sync-status/${jobId}`)
}

export async function getConnectorTables(customerId) {
  return get(`/api/connector/tables/${customerId}`)
}

export async function deleteTable(customerId, tableName) {
  return del(`/api/connector/tables/${customerId}/${encodeURIComponent(tableName)}`)
}

export async function resolveEndpointParams(customerId, endpointKey, vendorId, productId) {
  // vendorId/productId name the connection whose schema declares the endpoint's parameters
  const q = vendorId && productId
    ? `?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`
    : ''
  return get(`/api/connector/parameters/${customerId}/${endpointKey}${q}`)
}

export async function getEndpointSchema(vendor, product) {
  return get(`/api/connector/schema/${vendor}/${product}`)
}

export async function getEndpointCatalog(vendorId, productId, customerId) {
  // customerId is what makes the response carry last_synced_at and row_count per
  // endpoint — without it the Connectors page cannot show you what has landed.
  const q = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : ''
  return get(`/api/connector/endpoints/${encodeURIComponent(vendorId)}/${encodeURIComponent(productId)}${q}`)
}

// ─── Schema Diff Review (item 22, master-build-plan.md §22) ────────────────

// item 145 (second occurrence, AdminSchemaDiff.jsx): every {vendor, product} this
// platform actually has a config/schemas/{vendor}/{product}.json for -- NOT a
// hardcoded pair. Derived server-side from ConnectorSchemaLoader, the same
// filtered walk getSchemaDiff itself relies on, so this list can never offer a
// product getSchemaDiff would then 404 on.
export async function getSchemaDiffProducts() {
  return get('/api/admin/schema-diff-products')
}

export async function getSchemaDiff(vendor, product) {
  return get(`/api/admin/schema-diff?vendor=${encodeURIComponent(vendor)}&product=${encodeURIComponent(product)}`)
}

export async function decideSchemaDiffEntry({ vendor, product, path, method, decision, decidedBy, note, probeName }) {
  return post('/api/admin/schema-diff/decide', {
    vendor, product, path, method, decision, decided_by: decidedBy, note, probe_name: probeName,
  })
}

export async function getSchemaDiffDecisions(vendor, product) {
  return get(`/api/admin/schema-diff/decisions?vendor=${encodeURIComponent(vendor)}&product=${encodeURIComponent(product)}`)
}

// item 158 -- reads only what's already captured in storage (no live probe run, no
// vendor API round-trip), so it works even when live Dev Hub access is blocked.
// customerId, optional: Scott, 2026-09-27 -- "why would I want to do that one
// by one? Why can that exact same result come back when we run the get all
// endpoints." When given, each row comes back annotated with what actually
// happened last time this tenant's own probe run classified it
// (permission/works/not_found/never) -- see routers/admin.py's own docstring.
export async function getMissingGetAllSiblings(vendor, product, customerId) {
  const params = new URLSearchParams({ vendor, product })
  if (customerId) params.set('customer_id', customerId)
  return get(`/api/admin/schema-diff/missing-get-all-siblings?${params.toString()}`)
}

// item 158's auto-promote carve-out (Scott, 2026-09-26: "I don't want you promoting 1
// isolated endpoint... I'm bored with that 1 at a time nonsense"). dryRun defaults true,
// same convention as mergeIaCandidates -- always previews before an explicit write.
export async function autoPromoteGetAll(vendor, product, decidedBy, dryRun = true) {
  return post('/api/admin/schema-diff/auto-promote-get-all', {
    vendor, product, decided_by: decidedBy, dry_run: dryRun,
  })
}

// item 31: merges services.ia_entity_generator's generated IA-entity candidates into
// the live probe emission so they show up above as "new" rows. dryRun defaults true --
// this always reports what WOULD be added first; the caller must explicitly ask for
// dryRun=false to actually write.
export async function mergeIaCandidates(vendor, product, dryRun = true) {
  return post('/api/admin/schema-diff/merge-ia-candidates', { vendor, product, dry_run: dryRun })
}

// Stage 7 body discovery -- re-runs the full onboarding probe against a live customer
// connection with discover_bodies=true, so any needs_body endpoint gets a real chance
// at a proven request body before the next diff is computed. discoverBodies defaults
// true here because this IS the Stage 7 trigger; the backend route itself still
// defaults to false for every other caller.
export async function runProbeOnboarding(customerId, vendorId, productId, { discoverBodies = true, bodyMaxRounds = 6 } = {}) {
  return post(`/api/connector/debug-probe-onboarding/${encodeURIComponent(customerId)}`
    + `?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`
    + `&discover_bodies=${discoverBodies}&body_max_rounds=${bodyMaxRounds}`, {})
}

export async function getProbeOnboardingStatus(customerId, vendorId, productId) {
  return get(`/api/connector/debug-probe-onboarding-status/${encodeURIComponent(customerId)}`
    + `?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`)
}

// Every recorded run (last 20) for this customer/vendor/product, both finished and
// IN-FLIGHT -- real, in-platform visibility that doesn't depend on any single
// browser tab's polling state. See routers/connector.py's debug_probe_onboarding_history.
export async function getProbeOnboardingHistory(customerId, vendorId, productId) {
  return get(`/api/connector/debug-probe-onboarding-history/${encodeURIComponent(customerId)}`
    + `?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`)
}

// Actually stops an in-flight run (not just hides it from the UI) -- see
// routers/connector.py's debug_probe_onboarding_cancel. Omit runId to cancel every
// in-flight run for this customer/vendor/product. startedAt disambiguates a
// runId-less (legacy) cancel: it's the started_at the caller last saw on the
// stuck run, so a delayed/duplicate request can't land on a different, newer
// run that has since taken over the same status file.
export async function cancelProbeOnboarding(customerId, vendorId, productId, runId, startedAt) {
  const params = `?vendor_id=${encodeURIComponent(vendorId)}&product_id=${encodeURIComponent(productId)}`
    + (runId ? `&run_id=${encodeURIComponent(runId)}` : '')
    + (startedAt != null ? `&started_at=${encodeURIComponent(startedAt)}` : '')
  return post(`/api/connector/debug-probe-onboarding-cancel/${encodeURIComponent(customerId)}${params}`, {})
}

// ─── Threads ────────────────────────────────────────────────

export async function getThreads(customerId) {
  return get(`/api/threads${customerId ? `?customer_id=${customerId}` : ''}`)
}

export async function getThread(threadId) {
  return get(`/api/threads/${threadId}`)
}

export async function createThread(data) {
  return post('/api/threads', data)
}

export async function updateThread(threadId, data) {
  return put(`/api/threads/${threadId}`, data)
}

export async function deleteThread(threadId) {
  return del(`/api/threads/${threadId}`)
}

export async function runThread(threadId, params) {
  return post(`/api/threads/${threadId}/run`, params)
}

export async function getThreadExecutions(threadId) {
  return get(`/api/threads/${threadId}/executions`)
}

export async function startSession(customerId) {
  return post('/api/threads/sessions/start', { customer_id: customerId })
}

export async function sessionToThread(sessionId) {
  return post(`/api/threads/from-session/${sessionId}`, {})
}

// ─── Workplan ───────────────────────────────────────────────

// ─── Work Efforts ──────────────────────────────────────────

export async function createWorkEffort(customerId, title = 'New Work Effort') {
  return post('/api/work-efforts', { customer_id: customerId, title })
}

export async function getWorkEffort(effortId) {
  return get(`/api/work-efforts/${effortId}`)
}

export async function listWorkEfforts(customerId, state = null) {
  const params = [`customer_id=${customerId}`]
  if (state) params.push(`state=${state}`)
  return get(`/api/work-efforts?${params.join('&')}`)
}

export async function updateWorkEffort(effortId, data) {
  return put(`/api/work-efforts/${effortId}`, data)
}

export async function appendWorkEffortMessage(effortId, message) {
  return request(`/api/work-efforts/${effortId}/message`, {
    method: 'PATCH',
    body: JSON.stringify(message),
  })
}

export async function updateWorkEffortState(effortId, stateUpdate) {
  return request(`/api/work-efforts/${effortId}/state`, {
    method: 'PATCH',
    body: JSON.stringify(stateUpdate),
  })
}

export async function deleteWorkEffort(effortId) {
  return del(`/api/work-efforts/${effortId}`)
}

// ─── Thread Library (Work Effort Archive) ─────────────────

export async function getLibrary(params = {}) {
  const q = []
  if (params.customer_id) q.push(`customer_id=${params.customer_id}`)
  if (params.state) q.push(`state=${params.state}`)
  if (params.search) q.push(`search=${encodeURIComponent(params.search)}`)
  if (params.has_critical) q.push(`has_critical=true`)
  if (params.tag) q.push(`tag=${encodeURIComponent(params.tag)}`)
  if (params.sort) q.push(`sort=${params.sort}`)
  if (params.page) q.push(`page=${params.page}`)
  if (params.page_size) q.push(`page_size=${params.page_size}`)
  return get(`/api/work-efforts/library/all${q.length ? '?' + q.join('&') : ''}`)
}

export async function getLibraryStats() {
  return get('/api/work-efforts/library/stats')
}

export async function updateWorkEffortTags(effortId, tags) {
  return request(`/api/work-efforts/${effortId}/tags`, {
    method: 'PATCH', body: JSON.stringify({ tags }),
  })
}

export async function updateWorkEffortNotes(effortId, notes) {
  return request(`/api/work-efforts/${effortId}/notes`, {
    method: 'PATCH', body: JSON.stringify({ notes }),
  })
}

// ─── Orchestrator ──────────────────────────────────────────

export async function approveOrchApproach(sessionId) {
  return post(`/api/orchestrate/sessions/${sessionId}/approve`, { approve: true })
}

export async function getOrchSession(sessionId) {
  return get(`/api/orchestrate/sessions/${sessionId}`)
}

export async function submitOrchClarification(sessionId, concept, answer) {
  return post(`/api/orchestrate/sessions/${sessionId}/clarify`, { concept, answer })
}

// ─── Work Effort /run bridge (P1-1) ────────────────────────
// One endpoint, four actions. Prefer this over the three
// orchestrate helpers above — /run also persists state
// (plan_steps, running_steps, findings, completion_stats) to
// Supabase so the UI survives a page reload mid-investigation.
//
//   action: "start"    -> send question, get proposed plan back
//   action: "approve"  -> kick off execution (returns in ~1s,
//                         execution continues in background)
//   action: "refresh"  -> read live state (use for polling)
//   action: "clarify"  -> resume with clarification_response
//                         map {concept: answer}
export async function runWorkEffort(effortId, action, extras = {}) {
  return post(`/api/work-efforts/${effortId}/run`, {
    action,
    ...extras,
  })
}


export async function generateWorkplan(params) {
  return post('/api/workplan/generate', params)
}

export async function getWorkplan(workplanId) {
  return get(`/api/workplan/${workplanId}`)
}

export async function adjustWorkplan(workplanId, adjustments) {
  return post(`/api/workplan/${workplanId}/adjust`, adjustments)
}

export async function addWorkplanGate(workplanId, gate) {
  return post(`/api/workplan/${workplanId}/gate`, gate)
}

export async function getWorkplanCost(workplanId) {
  return get(`/api/workplan/${workplanId}/cost`)
}

export async function workplanToThread(workplanId) {
  return post(`/api/workplan/${workplanId}/convert-to-thread`, {})
}

// ─── Export ─────────────────────────────────────────────────

export async function getExportTemplates() {
  return get('/api/export/templates')
}

export async function generateExport(params) {
  return post('/api/export', params)
}

export async function getExportStatus(exportId) {
  return get(`/api/export/status/${exportId}`)
}

export function getExportDownloadUrl(exportId) {
  return `${API_BASE}/api/export/download/${exportId}`
}

export async function uploadExportTemplate(file, metadata) {
  const formData = new FormData()
  formData.append('file', file)
  if (metadata.name) formData.append('name', metadata.name)
  if (metadata.category) formData.append('category', metadata.category)
  if (metadata.visibility) formData.append('visibility', metadata.visibility)
  if (metadata.description) formData.append('description', metadata.description)
  if (metadata.vendor_id) formData.append('vendor_id', metadata.vendor_id)
  // Use the request() helper so auth headers are attached
  return request('/api/export/templates', {
    method: 'POST',
    body: formData,
  })
}

export async function getExportTemplate(templateId) {
  return get(`/api/export/templates/${templateId}`)
}

export async function deleteExportTemplate(templateId) {
  return del(`/api/export/templates/${templateId}`)
}

export function getExportTemplateDownloadUrl(templateId) {
  const base = API_BASE_CONFIG
  return `${base}/api/export/templates/${templateId}/download`
}

export async function uploadExportTemplateVersion(templateId, file) {
  const formData = new FormData()
  formData.append('file', file)
  return request(`/api/export/templates/${templateId}`, {
    method: 'PUT',
    body: formData,
  })
}

// ─── Compliance ─────────────────────────────────────────────

export async function getJurisdictions() {
  return get('/api/compliance/jurisdictions')
}

export async function getComplianceRules(params) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return get(`/api/compliance/rules${qs}`)
}

export async function runComplianceCheck(params) {
  return post('/api/compliance/check', params)
}

export async function getCustomerJurisdictions(customerId) {
  return get(`/api/compliance/customer/${customerId}/jurisdictions`)
}

// ─── Registers ──────────────────────────────────────────────

export async function getRegisters(customerId) {
  return get(`/api/registers/${customerId}`)
}

export async function uploadRegister(file, customerId, vendorFormat) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('customer_id', customerId)
  formData.append('vendor_format', vendorFormat)
  return post('/api/registers/upload', formData)
}

// ─── Documents ──────────────────────────────────────────────

export async function getDocuments(customerId) {
  return get(`/api/documents/list/${customerId}`)
}

export async function searchDocuments(customerId, query) {
  return get(`/api/documents/search/${customerId}?query=${encodeURIComponent(query)}`)
}

export async function uploadDocument(file, customerId) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('customer_id', customerId)
  return post('/api/documents/upload', formData)
}

export async function uploadDocumentAdvanced(file, customerId, truthType, vendorId, productId, functionalArea) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('customer_id', customerId)
  if (truthType) formData.append('truth_type', truthType)
  if (vendorId) formData.append('vendor_id', vendorId)
  if (productId) formData.append('product_id', productId)
  if (functionalArea) formData.append('functional_area', functionalArea)
  return post('/api/documents/upload', formData)
}

export async function deleteDocument(customerId, fileName) {
  return del(`/api/documents/${customerId}/${encodeURIComponent(fileName)}`)
}

export async function getDocumentChunks(baseDocId, chunkIndex = null, contextWindow = 2) {
  const params = []
  if (chunkIndex !== null) params.push(`chunk_index=${chunkIndex}`)
  params.push(`context_window=${contextWindow}`)
  return get(`/api/documents/chunks/${encodeURIComponent(baseDocId)}${params.length ? '?' + params.join('&') : ''}`)
}

// ─── Mappings ───────────────────────────────────────────────

export async function getMappings(customerId) {
  return get(`/api/mappings/${customerId}`)
}

export async function getMappingStats(customerId) {
  return get(`/api/mappings/${customerId}/stats`)
}

export async function suggestMappings(params) {
  return post('/api/mappings/suggest', params)
}

// ─── Quick Assess ───────────────────────────────────────────

export async function quickAssess(customerId) {
  return get(`/api/assess/${customerId}/quick`)
}

export async function getDashboard(customerId) {
  return get(`/api/dashboard/${customerId}`)
}

// ─── Admin ──────────────────────────────────────────────────

export async function getEngineStatus() {
  return get('/api/admin/engines/status')
}

export async function inspectQuery(params) {
  return post('/api/admin/inspect', params)
}

export async function rawQuery(sql, customerId) {
  return post('/api/admin/query/raw', { sql, customer_id: customerId })
}

export async function getMemoryState(customerId) {
  return get(`/api/admin/memory/${customerId}/state`)
}

// ─── Auth & User Management (Admin) ─────────────────────────

export async function getUsers(params) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : ''
  return get(`/api/auth/users${qs}`)
}

export async function inviteUser(data) {
  return post('/api/auth/invite', data)
}

export async function updateUserRole(userId, role) {
  return request(`/api/auth/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  })
}

export async function deactivateUser(userId) {
  return del(`/api/auth/users/${userId}`)
}

export async function reactivateUser(userId) {
  return post(`/api/auth/users/${userId}/reactivate`, {})
}

// The roles a user row may hold, in the backend's order; `machine` marks roles no person
// signs in as.
export async function getUserRoles() {
  return get('/api/auth/roles')
}

export async function assignUserCustomer(userId, customerId, relationship = 'assigned') {
  return post(`/api/auth/users/${userId}/customers`, { customer_id: customerId, relationship })
}

export async function unassignUserCustomer(userId, customerId) {
  return del(`/api/auth/users/${userId}/customers/${customerId}`)
}

export async function getCurrentUser() {
  return get('/api/auth/me')
}

// What the server ACTUALLY enforces (login mode, which roles need MFA, token lifetime, rate
// limits) -- the Security page renders this instead of hard-coded claims.
export async function getAuthConfig() {
  return get('/api/auth/config')
}

// Kept for older callers; there is no /api/auth/mfa-status endpoint.
export async function getMFAPolicy() {
  return getAuthConfig()
}

// ─── Default Export ─────────────────────────────────────────

const api = {
  getHealth,
  getCustomers, getCustomer, updateCustomer, createCustomer, createCustomerAutoId,
  previewDeleteCustomer, executeDeleteCustomer,
  getTerminology, getClarifications,
  chat, chatFeedback,
  getAnalysisModes, runAnalysis, compareFiles, compareTruths,
  explainVariance, getAnalysisHistory,
  getUploads, uploadFile, deleteUpload, hydrate, getHydrationStatus, getSchemas, getTablePreview,
  buildContextGraph, getContextGraphStatus, getContextGraphRelationships, getContextGraphJoinPath, getContextGraphMap,
  getPaycodeCrosswalk, getCustomerPaycodeMapping, saveCustomerPaycodeMapping,
  getConnections, getConnectorVendors, testConnection, saveConnection, deleteConnection,
  syncConnector, getSyncStatus, getConnectorTables,
  debugDevHubCapture, debugDevHubDrain, debugDevHubDrainStatus,
  startWfmUiLogin, completeWfmUiLogin,
  getThreads, getThread, createThread, updateThread, deleteThread,
  runThread, getThreadExecutions, startSession, sessionToThread,
  generateWorkplan, getWorkplan, adjustWorkplan, addWorkplanGate,
  getWorkplanCost, workplanToThread,
  getExportTemplates, generateExport, getExportStatus, getExportDownloadUrl,
  uploadExportTemplate, getExportTemplate, deleteExportTemplate,
  getJurisdictions, getComplianceRules, runComplianceCheck, getCustomerJurisdictions,
  getRegisters, uploadRegister,
  getDocuments, searchDocuments, uploadDocument, uploadDocumentAdvanced, deleteDocument,
  getDocumentChunks,
  getMappings, getMappingStats, suggestMappings,
  quickAssess, getDashboard,
  getEngineStatus, inspectQuery, rawQuery, getMemoryState,
  getUsers, inviteUser, updateUserRole, deactivateUser, reactivateUser, getUserRoles,
  assignUserCustomer, unassignUserCustomer, getCurrentUser, getMFAPolicy, getAuthConfig,
  createWorkEffort, getWorkEffort, listWorkEfforts, updateWorkEffort,
  appendWorkEffortMessage, updateWorkEffortState, deleteWorkEffort,
  getLibrary, getLibraryStats, updateWorkEffortTags, updateWorkEffortNotes,
  approveOrchApproach, getOrchSession, submitOrchClarification,
  runWorkEffort,
}

export default api



// ─── Pinned Metrics ───
export async function getPinnedMetrics(customerId) {
  return get(`/api/metrics/${customerId}`)
}

export async function executePinnedMetrics(customerId) {
  return get(`/api/metrics/${customerId}/execute`)
}

export async function pinMetric(customerId, data) {
  return post(`/api/metrics/${customerId}`, data)
}

export async function deletePinnedMetric(customerId, metricId) {
  return del(`/api/metrics/${customerId}/${metricId}`)
}

export async function getMetricCatalog() {
  return get('/api/metrics/catalog')
}

export async function getBackbone(customerId) {
  return get(`/api/backbone/${customerId}`)
}

export async function getRoles(customerId, entity) {
  const q = entity ? `?entity=${encodeURIComponent(entity)}` : ''
  return get(`/api/backbone/${customerId}/roles${q}`)
}

export async function getPeople(customerId, org) {
  return get(`/api/backbone/${customerId}/people?org=${encodeURIComponent(org || '')}`)
}

export async function getRollups(customerId, filters = {}) {
  return post(`/api/backbone/${customerId}/rollups`, { filters })
}

export async function getLanes(customerId, filters = {}) {
  return post(`/api/backbone/${customerId}/lanes`, { filters })
}

// Product-instance-grain integrity surface (§STEP 5/6). Returns products carrying
// derived layout primitives (stage / lane) and seams carrying kind — the React
// ladder is a pure renderer over this payload.
export async function getIntegrity2(customerId) {
  return post(`/api/backbone/${customerId}/integrity2`, {})
}

export async function runMetrics(customerId, metricKeys = [], filters = {}) {
  return post(`/api/metrics/${customerId}/run`, { metrics: metricKeys, filters })
}

export async function runMetricsStream(customerId, metricKeys = [], filters = {}, onMetric, signal) {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/api/metrics/${customerId}/run/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ metrics: metricKeys, filters }),
    signal,
  })
  if (!response.ok) throw new Error(`Stream failed: ${response.status}`)
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const lines = buf.split('\n')
    buf = lines.pop() // keep incomplete line
    for (const line of lines) {
      if (line.trim()) {
        try { onMetric(JSON.parse(line)) } catch (e) { /* skip malformed */ }
      }
    }
  }
  // flush remaining
  if (buf.trim()) {
    try { onMetric(JSON.parse(buf)) } catch (e) {}
  }
}

export async function drillMetric(customerId, metricKey, limit = 10000, month = null, filters = null) {
  const params = [`limit=${limit}`]
  if (month) params.push(`month=${encodeURIComponent(month)}`)
  if (filters && Object.keys(filters).length > 0) {
    params.push(`filters=${encodeURIComponent(JSON.stringify(filters))}`)
  }
  return get(`/api/metrics/${customerId}/drill/${metricKey}?${params.join('&')}`)
}

export async function getMetricCuration(customerId) {
  return get(`/api/metrics/${customerId}/curation`)
}

export async function saveMetricCuration(customerId, entries) {
  return put(`/api/metrics/${customerId}/curation`, { entries })
}

export async function saveMetricOverride(customerId, metricKey, overrides) {
  return put(`/api/metrics/${customerId}/curation/${metricKey}`, overrides)
}

// Payroll Register — customer-scoped; the server compiles every query (no SQL here)
export async function getRegisterPeriods(customerId) {
  return get(`/api/metrics/${encodeURIComponent(customerId)}/register/periods`)
}

export async function getRegisterChecks(customerId, payDate, payGroup) {
  const q = new URLSearchParams({ pay_date: payDate, pay_group: payGroup })
  return get(`/api/metrics/${encodeURIComponent(customerId)}/register/checks?${q}`)
}

export async function getRegisterCheckDetail(customerId, payIdentifier) {
  return get(`/api/metrics/${encodeURIComponent(customerId)}/register/checks/${encodeURIComponent(payIdentifier)}`)
}

// ─── Observations (auto-findings) ───
export async function getObservations(customerId, includeDismissed = false) {
  return get(`/api/observations/${customerId}?include_dismissed=${includeDismissed}`)
}

export async function getObservationCounts() {
  return get('/api/observations/batch/counts')
}

export async function generateObservations(customerId) {
  return get(`/api/observations/${customerId}/generate`)
}

// Staff only, and a reason is required (findings status v2): the server answers 400
// without one. Who dismissed it is the signed-in user.
export async function dismissObservation(observationId, reason) {
  return post(`/api/observations/${observationId}/dismiss`, { reason })
}

// ─── System Toggle ───
export async function getSystemTables(customerId, systemId = null) {
  const q = systemId ? `?system_id=${systemId}` : ''
  return get(`/api/context/${customerId}/systems/tables${q}`)
}

export async function getSystemMigrations(customerId) {
  return get(`/api/context/${customerId}/systems/migrations`)
}

export async function getProfileForSystem(customerId, systemId = null) {
  const q = systemId ? `?system=${systemId}` : ''
  return get(`/api/customers/${customerId}/profile${q}`)
}


// ─── Admin Onboarding (routers/admin_onboarding.py) ───
// The checklist by phase, the RaaS report library and the prewritten access requests.

const ONB = '/api/admin/onboarding'

export async function getOnboardingChecklist(customerId) {
  return get(`${ONB}/customers/${encodeURIComponent(customerId)}/checklist`)
}

export async function getOnboardingRequests(customerId) {
  return get(`${ONB}/customers/${encodeURIComponent(customerId)}/requests`)
}

export async function getOnboardingCustomerReports(customerId) {
  return get(`${ONB}/customers/${encodeURIComponent(customerId)}/reports`)
}

export async function getReportLibrary(includeArchived = false) {
  return get(`${ONB}/reports${includeArchived ? '?include_archived=true' : ''}`)
}

export async function uploadReportDefinition({ file, reportName, appliesWhen, description, note }) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('report_name', reportName || '')
  fd.append('applies_when', appliesWhen || '')
  fd.append('description', description || '')
  fd.append('note', note || '')
  return post(`${ONB}/reports`, fd)
}

export async function replaceReportDefinition(definitionId, file, note) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('note', note || '')
  return post(`${ONB}/reports/${encodeURIComponent(definitionId)}/versions`, fd)
}

export async function getReportVersions(definitionId) {
  return get(`${ONB}/reports/${encodeURIComponent(definitionId)}/versions`)
}

export async function archiveReportDefinition(definitionId, reason) {
  return post(`${ONB}/reports/${encodeURIComponent(definitionId)}/archive`, { reason: reason || '' })
}

export async function restoreReportDefinition(definitionId) {
  return post(`${ONB}/reports/${encodeURIComponent(definitionId)}/restore`, {})
}

// Who a report serves: 'checklist' (the checklist's rule, read live), 'every', or 'custom'
// with an applies_when. Each change is kept with who, when and why.
export async function setReportServes(definitionId, ruleSource, appliesWhen, reason) {
  return post(`${ONB}/reports/${encodeURIComponent(definitionId)}/serves`,
    { rule_source: ruleSource, applies_when: appliesWhen || null, reason: reason || '' })
}

// A download is fetched as a blob (the auth header cannot ride on a plain link), and its
// file name is read from Content-Disposition.
async function downloadBlob(path, fallbackName) {
  const authHeaders = await getAuthHeaders()
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders, cache: 'no-store' })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`API ${res.status}: ${body}`)
  }
  const cd = res.headers.get('content-disposition') || ''
  const m = cd.match(/filename="([^"]+)"/)
  return { blob: await res.blob(), filename: m ? m[1] : fallbackName }
}

export async function downloadReportDefinition(definitionId, version) {
  const q = version ? `?version=${version}` : ''
  return downloadBlob(`${ONB}/reports/${encodeURIComponent(definitionId)}/download${q}`, 'report')
}

export async function downloadCustomerReportsZip(customerId) {
  return downloadBlob(`${ONB}/customers/${encodeURIComponent(customerId)}/reports/zip`, 'reports.zip')
}
